import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Plus, X, Search, Filter, Trash2, Edit3, Palette,
  Users, Shield, Archive, RotateCcw, ExternalLink, ChevronRight, Copy, Check,
} from "lucide-react";

const TYPE_OPTIONS = [
  { v: "all", l: "Todos" },
  { v: "partner", l: "Partner" },
  { v: "white-label", l: "White-label" },
  { v: "dcc", l: "DCC" },
];

const STATUS_OPTIONS = [
  { v: "active", l: "Activos" },
  { v: "all", l: "Incl. archivados" },
];

function TypeBadge({ type }) {
  const map = {
    "partner": "bg-brand-orange-500/20 text-brand-orange-500 border-brand-orange-500/40",
    "white-label": "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    "dcc": "bg-purple-500/20 text-purple-300 border-purple-500/40",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold rounded border ${map[type] || "bg-gray-700/40 text-gray-400 border-gray-700"}`}>
      {type}
    </span>
  );
}

export default function SuperadminTenants() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter && typeFilter !== "all") params.set("type", typeFilter);
      if (statusFilter === "all") params.set("include_archived", "true");
      const { data } = await api.get(`/admin/tenants?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al cargar tenants");
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (slug) => {
    if (!window.confirm(`¿Archivar el tenant "${slug}"? (Se marcará is_active=false, no se elimina)`)) return;
    try {
      await api.delete(`/admin/tenants/${slug}`);
      toast.success("Tenant archivado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.2 · Enterprise B2B</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
              <Building2 className="w-7 h-7" /> Tenants / Partners
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {total} instituciones · gestión multi-tenant unificada
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="tenant-new-btn"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo tenant
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 p-4 bg-[#111827] border border-gray-800 rounded-xl">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text" placeholder="Buscar por nombre o slug…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="tenants-search-input"
              className="w-full pl-9 pr-3 py-2 bg-[#0B0F14] border border-gray-800 rounded-md text-sm text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            {TYPE_OPTIONS.map((o) => (
              <button key={o.v} onClick={() => setTypeFilter(o.v)}
                data-testid={`tenants-filter-type-${o.v}`}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  typeFilter === o.v ? "bg-brand-orange-600 text-white border-brand-orange-600"
                  : "text-gray-400 border-gray-700 hover:border-brand-orange-600"
                }`}>{o.l}</button>
            ))}
            <span className="w-px h-6 bg-gray-700 mx-2" />
            {STATUS_OPTIONS.map((o) => (
              <button key={o.v} onClick={() => setStatusFilter(o.v)}
                data-testid={`tenants-filter-status-${o.v}`}
                className={`px-3 py-1.5 text-xs rounded-md border ${
                  statusFilter === o.v ? "bg-emerald-600 text-white border-emerald-600"
                  : "text-gray-400 border-gray-700"
                }`}>{o.l}</button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-hidden" data-testid="tenants-table">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">Sin resultados</div>
          ) : (
            <table className="w-full">
              <thead className="bg-[#0B0F14] border-b border-gray-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Tenant</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Tipo</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">License prefix</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Admins</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Alumnos</th>
                  <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
                  <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((t) => (
                  <tr key={t.id}
                    data-testid={`tenant-row-${t.slug}`}
                    className="hover:bg-[#0B0F14] cursor-pointer transition-colors"
                    onClick={() => setSelectedSlug(t.slug)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: t.branding?.primary_color || "#DC5A0F" }}>
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <div className="text-white font-semibold text-sm" data-testid={`tenant-name-${t.slug}`}>{t.name}</div>
                          <div className="text-xs text-gray-500 font-mono">/t/{t.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><TypeBadge type={t.type} /></td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-300">{t.license_prefix || "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300" data-testid={`tenant-admins-count-${t.slug}`}>
                      <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3 text-brand-orange-500" />{t.admins_count}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300" data-testid={`tenant-alumnos-count-${t.slug}`}>
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-emerald-400" />{t.alumnos_count}</span>
                    </td>
                    <td className="px-4 py-3">
                      {t.is_active === false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold rounded border bg-gray-700/40 text-gray-400 border-gray-700">
                          <Archive className="w-3 h-3" /> Archivado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                          <Check className="w-3 h-3" /> Activo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => window.open(`/l/${t.slug}`, "_blank")}
                          data-testid={`tenant-view-${t.slug}`}
                          className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded"
                          title="Ver landing pública">
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSelectedSlug(t.slug)}
                          data-testid={`tenant-edit-${t.slug}`}
                          className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded"
                          title="Editar">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {superadmin && t.is_active !== false && (
                          <button
                            onClick={() => onDelete(t.slug)}
                            data-testid={`tenant-delete-${t.slug}`}
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                            title="Archivar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-600 ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showModal && <NewTenantModal onClose={() => { setShowModal(false); load(); }} />}
      {selectedSlug && <TenantDetailModal slug={selectedSlug} onClose={() => { setSelectedSlug(null); load(); }} />}
    </StaffLayout>
  );
}

// ----------------------------- NEW TENANT MODAL -----------------------------
function NewTenantModal({ onClose }) {
  const [form, setForm] = useState({
    name: "",
    slug: "",
    type: "partner",
    license_prefix: "",
    subdomain: "",
    primary_color: "#DC5A0F",
    secondary_color: "#0B0B0F",
    logo_url: "",
    admin_email: "",
    admin_name: "",
    send_welcome: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Requerido";
    if (!form.slug.trim()) e.slug = "Requerido";
    if (form.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) e.slug = "kebab-case: solo minúsculas, números, guiones";
    if (form.license_prefix && !/^[A-Z0-9]{2,10}(?:-[A-Z0-9]{1,10})?$/i.test(form.license_prefix)) e.license_prefix = "2-10 chars A-Z0-9, opcional un guion";
    if (!form.admin_email.trim()) e.admin_email = "Requerido";
    if (!form.admin_name.trim()) e.admin_name = "Requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/tenants", {
        name: form.name,
        slug: form.slug,
        type: form.type,
        license_prefix: form.license_prefix || null,
        subdomain: form.subdomain || null,
        branding: {
          primary_color: form.primary_color,
          secondary_color: form.secondary_color,
          logo_url: form.logo_url || null,
        },
        first_admin: {
          email: form.admin_email,
          full_name: form.admin_name,
          send_welcome_email: form.send_welcome,
        },
      });
      setResult(data);
      toast.success("Tenant + admin creados");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al crear");
    } finally {
      setSubmitting(false);
    }
  };

  const [copied, setCopied] = useState(false);
  const copyPwd = () => {
    if (!result?.first_admin?.temp_password) return;
    navigator.clipboard.writeText(result.first_admin.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(ev) => ev.stopPropagation()}
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 my-8"
        data-testid="new-tenant-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white font-display">Nuevo tenant</h3>
            <p className="text-xs text-gray-500 mt-1">Crea el tenant + primer admin en un solo paso</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Nombre" testid="tenant-form-name" required error={errors.name}>
                <input required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="tenant-form-name"
                  className="input" placeholder="Trafalgar Group" />
              </FormField>
              <FormField label="Slug (kebab-case)" testid="tenant-form-slug" required error={errors.slug}>
                <input required value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
                  data-testid="tenant-form-slug"
                  className="input font-mono" placeholder="trafalgar-group" />
                <p className="mt-1 text-[10px] text-gray-500">URL pública: <span className="font-mono text-brand-orange-500">/t/{form.slug || "…"}</span></p>
              </FormField>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Tipo">
                <select value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="tenant-form-type"
                  className="input">
                  <option value="partner">Partner</option>
                  <option value="white-label">White-label</option>
                </select>
              </FormField>
              <FormField label="License prefix" error={errors.license_prefix}>
                <input value={form.license_prefix}
                  onChange={(e) => setForm({ ...form, license_prefix: e.target.value.toUpperCase() })}
                  data-testid="tenant-form-license-prefix"
                  className="input font-mono uppercase" placeholder="TRA-SOF" />
              </FormField>
              <FormField label="Subdomain (informativo)">
                <input value={form.subdomain}
                  onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase() })}
                  className="input font-mono" placeholder="academy.tenant.mx" />
              </FormField>
            </div>

            <div className="pt-2 border-t border-gray-800">
              <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1">
                <Palette className="w-3 h-3" /> Branding inicial
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-gray-400">Primary</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      data-testid="tenant-form-primary-color"
                      className="h-10 w-14 bg-transparent border border-gray-800 rounded cursor-pointer" />
                    <input type="text" value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      className="input font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-gray-400">Secondary</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="h-10 w-14 bg-transparent border border-gray-800 rounded cursor-pointer" />
                    <input type="text" value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="input font-mono flex-1" />
                  </div>
                </div>
                <FormField label="Logo URL (opc.)">
                  <input value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    className="input" placeholder="https://…" />
                </FormField>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-800">
              <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1">
                <Shield className="w-3 h-3" /> Primer admin del tenant
              </p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email admin" required error={errors.admin_email}>
                  <input required type="email" value={form.admin_email}
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value.toLowerCase() })}
                    data-testid="tenant-form-admin-email"
                    className="input" placeholder="rh@tenant.mx" />
                </FormField>
                <FormField label="Nombre admin" required error={errors.admin_name}>
                  <input required value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    data-testid="tenant-form-admin-name"
                    className="input" placeholder="RH Trafalgar" />
                </FormField>
              </div>
              <label className="mt-3 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.send_welcome}
                  onChange={(e) => setForm({ ...form, send_welcome: e.target.checked })}
                  data-testid="tenant-form-send-welcome"
                  className="w-4 h-4 accent-brand-orange-600" />
                <span className="text-sm text-gray-300">Enviar email de bienvenida <span className="text-brand-orange-500 text-xs">(MOCKED)</span></span>
              </label>
            </div>

            <button type="submit" disabled={submitting}
              data-testid="tenant-form-submit"
              className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
              {submitting ? "Creando…" : "Crear tenant + admin"}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/40 rounded-lg">
              <p className="text-emerald-300 font-semibold text-sm">✓ Tenant creado exitosamente</p>
              <p className="text-xs text-emerald-300/80 mt-1">Slug: <span className="font-mono">{result.tenant_slug}</span></p>
            </div>
            {result.first_admin && (
              <div className="p-4 bg-[#0B0F14] border border-gray-700 rounded-lg space-y-2">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Credenciales del admin</p>
                <div className="text-sm font-mono text-white">Email: {result.first_admin.email}</div>
                {result.first_admin.temp_password ? (
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-mono text-brand-orange-500 flex-1">Password: {result.first_admin.temp_password}</div>
                    <button onClick={copyPwd} data-testid="tenant-copy-password"
                      className="p-1.5 rounded border border-gray-700 hover:border-brand-orange-500 text-gray-400 hover:text-brand-orange-500">
                      {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-400">Usuario existente · se agregó a este tenant.</div>
                )}
                <p className="text-[11px] text-gray-500 pt-2 border-t border-gray-800">
                  El admin recibió el email de bienvenida <span className="text-brand-orange-500">(MOCKED)</span>. Verifícalo en <span className="font-mono">Email marketing → Deliveries</span>.
                </p>
              </div>
            )}
            <button onClick={onClose}
              className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">
              Cerrar
            </button>
          </div>
        )}
      </div>
      <style>{`.input { width:100%; margin-top:4px; background:#0B0F14; border:1px solid #1F2937; border-radius:6px; padding:8px 12px; font-size:14px; color:#FFF; }
      .input:focus { outline: 2px solid #DC5A0F; outline-offset: 1px; }`}</style>
    </div>
  );
}

function FormField({ label, children, required, error, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-gray-400 flex items-center gap-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400" data-testid={`${testid}-error`}>{error}</p>}
    </div>
  );
}

// ----------------------------- TENANT DETAIL -----------------------------
function TenantDetailModal({ slug, onClose }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/tenants/${slug}`);
      setDetail(data);
      setForm({
        name: data.name,
        type: data.type,
        license_prefix: data.license_prefix || "",
        subdomain: data.subdomain || "",
        primary_color: (data.branding || {}).primary_color || "#DC5A0F",
        secondary_color: (data.branding || {}).secondary_color || "#0B0B0F",
        logo_url: (data.branding || {}).logo_url || "",
      });
    } catch (e) {
      toast.error("No se pudo cargar el tenant");
      onClose();
    }
  }, [slug, onClose]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${slug}`, {
        name: form.name, type: form.type,
        license_prefix: form.license_prefix || null,
        subdomain: form.subdomain || null,
        branding: {
          primary_color: form.primary_color,
          secondary_color: form.secondary_color,
          logo_url: form.logo_url || null,
        },
      });
      toast.success("Guardado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const addAdmin = async () => {
    const email = window.prompt("Email del admin a agregar:");
    if (!email) return;
    try {
      const r = await api.post(`/admin/tenants/${slug}/admins`, { email, send_welcome_email: true });
      if (r.data.temp_password) {
        toast.success(`Admin creado · password: ${r.data.temp_password}`, { duration: 10000 });
      } else {
        toast.success(`Admin agregado al tenant (status: ${r.data.status})`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeAdmin = async (uid, email) => {
    if (!window.confirm(`¿Remover a ${email} de este tenant? (No se elimina el usuario.)`)) return;
    try {
      await api.delete(`/admin/tenants/${slug}/admins/${uid}`);
      toast.success("Admin removido");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#111827] border border-gray-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="tenant-detail-modal">
        {/* Header */}
        <div className="p-6 border-b border-gray-800 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: form.primary_color }}>
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white font-display">{detail.name}</h3>
              <p className="text-xs text-gray-500 font-mono">/t/{detail.slug} · {detail.admins_count} admins · {detail.alumnos_count} alumnos</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        {/* Tabs */}
        <div className="px-6 border-b border-gray-800 flex gap-4">
          {["info", "admins", "branding"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              data-testid={`tenant-detail-tab-${t}`}
              className={`py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === t ? "border-brand-orange-600 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
              }`}>
              {t === "info" ? "Info general" : t === "admins" ? `Admins (${detail.admins_count})` : "Branding"}
            </button>
          ))}
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === "info" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nombre">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="tenant-detail-name-input" className="input" />
                </FormField>
                <FormField label="Tipo">
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
                    <option value="partner">Partner</option>
                    <option value="white-label">White-label</option>
                    <option value="dcc">DCC</option>
                  </select>
                </FormField>
                <FormField label="License prefix">
                  <input value={form.license_prefix} onChange={(e) => setForm({ ...form, license_prefix: e.target.value.toUpperCase() })}
                    className="input font-mono uppercase" />
                </FormField>
                <FormField label="Subdomain (informativo)">
                  <input value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value })}
                    className="input font-mono" />
                </FormField>
              </div>
              <button onClick={save} disabled={saving}
                data-testid="tenant-detail-save-btn"
                className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
                {saving ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          )}
          {tab === "admins" && (
            <div className="space-y-3">
              <button onClick={addAdmin}
                data-testid="tenant-detail-add-admin-btn"
                className="w-full py-2 rounded-md border border-brand-orange-600 text-brand-orange-500 hover:bg-brand-orange-600/10 text-sm font-semibold inline-flex items-center justify-center gap-1">
                <Plus className="w-4 h-4" /> Agregar admin
              </button>
              {(detail.admins || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between p-3 bg-[#0B0F14] border border-gray-800 rounded"
                  data-testid={`tenant-admin-row-${a.email}`}>
                  <div>
                    <div className="text-sm text-white font-semibold">{a.full_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{a.email}</div>
                    {a.tenant_ids.length > 1 && (
                      <div className="text-[10px] text-brand-orange-500 mt-1 font-semibold">
                        Multi-tenant · {a.tenant_ids.length} tenants
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeAdmin(a.id, a.email)}
                    data-testid={`tenant-admin-remove-${a.email}`}
                    className="text-red-400 hover:text-red-500 text-sm inline-flex items-center gap-1">
                    <Trash2 className="w-4 h-4" /> Quitar
                  </button>
                </div>
              ))}
            </div>
          )}
          {tab === "branding" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-gray-400">Primary color</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      data-testid="tenant-detail-primary-color"
                      className="h-10 w-14 bg-transparent border border-gray-800 rounded cursor-pointer" />
                    <input type="text" value={form.primary_color}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      className="input font-mono flex-1" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-gray-400">Secondary color</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="h-10 w-14 bg-transparent border border-gray-800 rounded cursor-pointer" />
                    <input type="text" value={form.secondary_color}
                      onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                      className="input font-mono flex-1" />
                  </div>
                </div>
              </div>
              <FormField label="Logo URL">
                <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  className="input" placeholder="https://…" />
              </FormField>
              <div className="p-4 rounded-lg" style={{ background: form.primary_color }}>
                <div className="flex items-center gap-3">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="w-10 h-10 object-contain rounded bg-white/20 p-1" />
                  ) : (
                    <div className="w-10 h-10 rounded flex items-center justify-center" style={{ background: form.secondary_color }}>
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <div className="text-white font-bold">{form.name || "Preview"}</div>
                    <div className="text-xs text-white/70">Vista previa del branding aplicado</div>
                  </div>
                </div>
              </div>
              <button onClick={save} disabled={saving}
                data-testid="tenant-detail-branding-save"
                className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
                {saving ? "Guardando…" : "Guardar branding"}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`.input { width:100%; margin-top:4px; background:#0B0F14; border:1px solid #1F2937; border-radius:6px; padding:8px 12px; font-size:14px; color:#FFF; }
      .input:focus { outline: 2px solid #DC5A0F; outline-offset: 1px; }`}</style>
    </div>
  );
}
